import { Injectable } from '@angular/core';

import {
    AdvancedSearchGroup,
    AdvancedSearchTerm,
    ApiQuery,
    ApiQueryBuilder,
    EmptyStructuredQuery,
    FacetsToQueryParts,
    isNotNil,
    LogicalOperand,
    MergeStructuredQueries,
    Operand,
    OperandAsString,
    QueryHandleQuotes,
    QueryPart,
    QueryPartData,
    QueryPartStrategy,
    SanitizeFieldSearchValue,
    StructuredQuery
} from '@stylehaven/components';

import { DIGIHAVEN_PROTOCOL, LOCATION_LEGACY_PROTOCOL } from './constants';
import { IsDateRangeQuery } from './date-range-query-helper';
import { DigihavenFakeQueryPartFields, DigihavenQueryPartsHelper } from './digihaven-query-parts.helper';

@Injectable()
export class LuceneHelper extends ApiQueryBuilder {
    private readonly SELECT_ALL_OPERATOR: string = '*:*';

    // NOTE: Similar to ApiQueryBuilder!
    // TODO: Deprecate and replace by shared ApiQueryBuilder
    public queryToLuceneQuery(...query: ApiQuery[]): string {
        let mergedQuery: StructuredQuery = EmptyStructuredQuery;

        for (const current of query) {
            mergedQuery = MergeStructuredQueries(mergedQuery, current.structuredQuery);
        }
        const q: string = LuceneHelper.buildGlobalSearchQuery(new ApiQuery(mergedQuery));

        // Replace the ChildrenAndCandidateChildren faked field
        // Alternatively if this does not work, do it inside QueryPartsToStructuredQuery?
        const fakedFieldValues = mergedQuery.AdvancedSearch.Groups.flatMap(group => group.Terms).find(
            term => term?.DottedKey === DigihavenFakeQueryPartFields.ChildrenAndCandidateChildren
        )?.Values;

        const withoutFakedGroups: AdvancedSearchGroup[] = mergedQuery.AdvancedSearch.Groups.map(group => ({
            ...group,
            Terms: (group.Terms ?? []).filter(
                term => term?.DottedKey !== DigihavenFakeQueryPartFields.ChildrenAndCandidateChildren
            )
        }));

        if (fakedFieldValues) {
            withoutFakedGroups.push(this.getFakedAdvancedSearchGroup(fakedFieldValues));
        }

        const advQ: string = ApiQueryBuilder.buildAdvancedSearchQuery(
            new ApiQuery({
                ...EmptyStructuredQuery,
                AdvancedSearch: {
                    ...mergedQuery.AdvancedSearch,
                    Groups: withoutFakedGroups
                }
            })
        );

        const restrictions = query
            .map(query => query.restriction)
            .filter(isNotNil)
            .map(restriction =>
                OperandAsString<QueryPart>(restriction, queryPart =>
                    ApiQueryBuilder.getQueryPartAsLuceneQuery(queryPart)
                )
            );

        const selectAllQuery: string = this.shouldAddSelectAllOperator(withoutFakedGroups)
            ? this.SELECT_ALL_OPERATOR
            : '';

        const facetQuery = LuceneHelper.facetQueryToLuceneQuery(new ApiQuery(mergedQuery));

        const columnFilterQuery = LuceneHelper.columnFilterQueryToLuceneQuery(new ApiQuery(mergedQuery));

        return [q, advQ, ...restrictions, selectAllQuery, facetQuery, columnFilterQuery]
            .filter(queryPart => queryPart !== '')
            .join(' ')
            .trim();
    }

    public static columnFilterQueryToLuceneQuery(query: ApiQuery): string {
        return ApiQueryBuilder.columnFiltersToLuceneQuery(query.structuredQuery.ColumnFilters);
    }

    public static facetQueryToLuceneQuery(query: ApiQuery): string {
        // Still uses QP for now
        const facets: QueryPartData[] = FacetsToQueryParts(query.structuredQuery.Facets);

        // MED-5510 + MED-5636: facet values should be escaped unless they are exclusively wildcards
        const facetQueries: string[] = facets.map(facet => {
            // MED-5833: Catch 'missing' value
            // Value will be 'No {field}' since that is what API returns
            // Since we don't work with other Excluding facets anyway, we always default to '*'
            const processedFacet =
                facet.strategy === QueryPartStrategy.MatchNone
                    ? {
                          ...facet,
                          values: ['*']
                      }
                    : facet;

            return this.isFacetWildcardValue(processedFacet)
                ? this.getQueryPartAsLuceneQuery(
                      new QueryPart({ ...processedFacet, handleQuotes: QueryHandleQuotes.Escape })
                  )
                : this.getQueryPartAsLuceneQuery(
                      new QueryPart({ ...processedFacet, handleQuotes: QueryHandleQuotes.UseQuotes })
                  );
        });

        return facetQueries.join(' ');
    }

    // MED-5636: 'No ...' searches use a wildcard. These should not be escaped.
    private static isFacetWildcardValue(facet: QueryPartData): boolean {
        return facet.values.every(value => this.WILDCARD_VALUES.includes(value));
    }

    private getFakedAdvancedSearchGroup(values: string[]): AdvancedSearchGroup {
        // Only used for ChildrenAndCandidateChildren

        const termFields: string[] = DigihavenQueryPartsHelper.getActualCorrespondingQueryPartFields(
            DigihavenFakeQueryPartFields.ChildrenAndCandidateChildren
        );
        const terms: AdvancedSearchTerm[] = termFields.map(termField => ({
            DottedKey: termField,
            Operand: Operand.Include,
            Values: values
        }));
        return {
            LogicalOperand: LogicalOperand.Or,
            Terms: terms
        };
    }

    private globalQueryToLuceneQuery(mergedQuery: StructuredQuery): string {
        return `${mergedQuery.Global.join(',')}`;
    }

    private shouldAddSelectAllOperator(advancedSearchGroups: AdvancedSearchGroup[]): boolean {
        return (
            advancedSearchGroups.length > 0 &&
            advancedSearchGroups.every(group => group.Terms?.every(term => term.Operand === Operand.Exclude))
        );
    }

    public static sanitizeValue(value: string): string {
        if (IsDateRangeQuery(value)) {
            return value;
        }

        let updatedValue = value;
        updatedValue = this.removeProtocol(value, updatedValue, DIGIHAVEN_PROTOCOL);
        updatedValue = this.removeProtocol(value, updatedValue, LOCATION_LEGACY_PROTOCOL);
        return SanitizeFieldSearchValue(updatedValue);
    }

    private static removeProtocol(value: string, updatedValue: string, protocol: string) {
        if (value.startsWith(protocol)) {
            const withoutQuery = value.split('?')[0];
            const withoutProtocol: string = withoutQuery.replace(protocol, '');
            updatedValue = `${withoutProtocol}*`;
        }
        return updatedValue;
    }
}
